import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { EmitirSuscripcion } from '@interfaces/emitir-suscripcion.interface';
import { downloadBlobFile } from '@utils/file.util';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { CotizacionService } from './cotizacion.service';
import { ComunService } from '@services/comun.service';
import { ConveniosService } from './convenios.service';
import { GpsService } from './gps.service';
import { EmisionResponse } from '@interfaces/emision-response.interface';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class PolizaService {
  constructor(
    private httpBackend: HttpBackend,
    private http: HttpClient,
    private cotizacionService: CotizacionService,
    private comunService: ComunService,
    private convenioService: ConveniosService,
    private gpsService: GpsService,
    private router: Router
  ) {}

  emisionWebPay(parametro: string): Observable<EmisionResponse> {
    return this.emitirPoliza('Poliza/EmisionWebPay', parametro);
  }

  emitirSuscripcion(parametro: string): Observable<EmisionResponse> {
    return this.emitirPoliza('Poliza/EmisionSuscripcion', parametro);
  }

  private emitirPoliza(
    endpoint: string,
    parametro: string
  ): Observable<EmisionResponse> {
    const http = new HttpClient(this.httpBackend);
    return http
      .post<EmitirSuscripcion>(`${environment.URL_API}/${endpoint}`, {
        parametro,
      })
      .pipe(
        switchMap((response) => this.validarTransaccion(response)),
        switchMap((response) => this.procesarCotizacion(response)),
        switchMap((datos) => this.procesarConvenio(datos)),
        switchMap((datos) => this.procesarGps(datos))
      );
  }

  obtenerPdf(cotizacionId: string, nombreArchivo: string) {
    return this.http
      .post(
        '/Poliza/ObtenerPdf',
        {
          parametro: cotizacionId.toString(),
        },
        { responseType: 'blob' }
      )
      .pipe(
        tap((response) => {
          downloadBlobFile(
            response,
            `Comprobante de contratación ${nombreArchivo}.pdf`,
            true
          );
        })
      );
  }

  private validarTransaccion(response: EmitirSuscripcion) {
    if (response === null) {
      this.router.navigateByUrl('/error');
    }

    return of(response);
  }

  private procesarCotizacion(response: EmitirSuscripcion): Observable<any> {
    return this.cotizacionService
      .obtenerDatosComprobante(response.idCotizacion)
      .pipe(
        map((comprobante) => ({
          datosSuscripcion: response,
          datosAsegurado: comprobante,
          idCotizacion: response.idCotizacion,
        }))
      );
  }

  private procesarConvenio(datos: any): Observable<any> {
    const convenioIdEncriptado = this.comunService.encriptar(
      datos.datosAsegurado.convenioId.toString()
    );
    return this.convenioService
      .obtenerParametrosDelConvenio(convenioIdEncriptado)
      .pipe(map((convenio) => ({ ...datos, convenio })));
  }

  private procesarGps(datos: any) {
    const solicitudGps = {
      contratante: {
        rut: datos.datosAsegurado.contratantePoliza.rut,
        nombreCompleto: datos.datosSuscripcion.nombreAsegurado,
        region: datos.datosAsegurado.contratantePoliza.region,
        comuna: datos.datosAsegurado.contratantePoliza.comuna,
        direccion: datos.datosAsegurado.contratantePoliza.direccion,
        email: datos.datosAsegurado.contratantePoliza.email,
        telefono: datos.datosAsegurado.contratantePoliza.telefono,
      },
      vehiculos: [
        {
          anio: datos.datosAsegurado.vehiculoPoliza.anio,
          estadoVh: datos.datosAsegurado.vehiculoPoliza.estado,
          marca: datos.datosAsegurado.vehiculoPoliza.marca,
          modelo: datos.datosAsegurado.vehiculoPoliza.modelo,
          nombreCompletoAsegurado: datos.datosSuscripcion.nombreAsegurado,
          patente: datos.datosAsegurado.vehiculoPoliza.patente,
          rutAsegurado: datos.datosAsegurado.contratantePoliza.rut,
        },
      ],
      nroCotizacion: datos.datosSuscripcion.numeroCotizacion,
      idCotizacion: datos.idCotizacion,
      nroPoliza: datos.datosSuscripcion.numeroPoliza,
    };
    return this.gpsService
      .solicitarGps(solicitudGps)
      .pipe(map((gps) => ({ ...datos, gps })));
  }
}
